import { FormHelperText, type TextFieldProps } from '@mui/material';
import {
  DatePicker as MuiDatePicker,
  type DateValidationError,
  LocalizationProvider,
  type PickerChangeHandlerContext,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useDebounce } from '@uidotdev/usehooks';
import { type Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';

import { dayjs } from '~/utils/datetime';
import { dateFull, LOCALE_INFO } from '~/utils/l10n';

type P = {
  readonly isDisabled?: boolean;
  readonly isRequired?: boolean;
  readonly maxDate?: Date;
  readonly minDate?: Date;
  readonly onChange: (value: Date | undefined) => void;
  readonly textFieldProps?: TextFieldProps;
  readonly value: Date | undefined;
};

export const DatePicker = ({
  isDisabled,
  isRequired,
  maxDate = '2099-12-31',
  minDate = '1900-01-01',
  onChange,
  textFieldProps,
  value,
}: P) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localValue, setLocalValue] = useState<Dayjs | undefined>(
    value ? dayjs(value) : undefined,
  );
  const [isDirty, setIsDirty] = useState(false);
  const debouncedValue = useDebounce(localValue, 500);
  const originalValue = useRef(value);
  const [error, setError] = useState<DateValidationError | undefined>(
    undefined,
  );
  const debouncedError = useDebounce(error, 200);

  // Update local value when external value changes and component is not dirty
  useEffect(() => {
    if (!isDirty && value !== originalValue.current) {
      setLocalValue(value ? dayjs(value) : undefined);
      originalValue.current = value;
    }
  }, [value, isDirty]);

  // Propagate changes up when debounced value changes
  useEffect(() => {
    if (!isDirty || debouncedValue === undefined) {
      return;
    }

    const isValidDate = debouncedValue.isValid();
    if (isValidDate) {
      const newDate = debouncedValue.toDate();
      const originalDate = originalValue.current
        ? new Date(originalValue.current)
        : undefined;

      if (newDate.getTime() !== originalDate?.getTime()) {
        onChange(newDate);
      }
    } else {
      onChange(originalValue.current);
    }
  }, [debouncedValue, isDirty]);

  useEffect(() => {
    // resetting the value to the original in case of an error, to prevent the user from saving an "unknown" date by mistake
    // Since they might have chosen a correct date before and this is what will be store in the local value
    if (originalValue.current) {
      onChange(new Date(originalValue.current));
    }
  }, [error]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleChange = (
    newValue: Dayjs | undefined,
    context: PickerChangeHandlerContext<DateValidationError>,
  ) => {
    if (!isDirty) {
      setIsDirty(true);
    }

    if (newValue?.isValid() && !context.validationError) {
      setLocalValue(newValue);
    }
  };

  function poorMansErrorMessage(errorName: string) {
    switch (errorName) {
      case 'invalidDate':
        return 'Ungültiges Datum - Änderungen werden nicht gespeichert';

      case 'minDate':
        return `Mindestdatum unterschritten (${dayjs(minDate).format('DD.MM.YYYY')})`;

      case 'maxDate':
        return `Höchstdatum überschritten (${dayjs(maxDate).format('DD.MM.YYYY')})`;

      default:
        return undefined;
    }
  }

  return (
    <LocalizationProvider
      adapterLocale={LOCALE_INFO.name}
      dateAdapter={AdapterDayjs}
    >
      <div className="relative">
        <MuiDatePicker
          dayOfWeekFormatter={(day) => day.format('dd')}
          disabled={isDisabled}
          format={dateFull}
          maxDate={maxDate ? dayjs(maxDate) : undefined}
          minDate={minDate ? dayjs(minDate) : undefined}
          open={isOpen}
          slotProps={{
            textField: {
              error: Boolean(debouncedError),
              ...textFieldProps,
              required: isRequired,
            },
          }}
          value={localValue ?? null}
          onChange={handleChange}
          onClose={handleClose}
          onError={(newError) => {
            setError(newError);
          }}
          onOpen={handleOpen}
        />
        {debouncedError ? (
          <div className="flex w-full justify-end">
            <FormHelperText className="text-mui-error-red absolute z-10 bg-white/90 px-1 leading-4">
              {poorMansErrorMessage(debouncedError)}
            </FormHelperText>
          </div>
        ) : null}
      </div>
    </LocalizationProvider>
  );
};
